/**
 * HighlightsSidebar Component
 *
 * Displays a list of highlights for an article with options to navigate,
 * delete, and manage highlights.
 */

import React, { useState } from 'react';
import { formatFullDate } from '../utils/dateUtils';
import EmptyState from './EmptyState';
import { useConfirm } from '../hooks/useConfirm';

const HighlightsSidebar = ({
  highlights = [],
  isOpen,
  onClose,
  onHighlightClick,
  onDeleteHighlight,
  onDeleteAll,
  detachmentInfo,
  onReattach,
  isLoading = false,
  isOffline = false,
}) => {
  const confirm = useConfirm();
  const [deletingId, setDeletingId] = useState(null);
  const [isReattaching, setIsReattaching] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const handleDelete = async (highlightId, event) => {
    event.stopPropagation();

    if (!(await confirm({
      title: 'Delete highlight?',
      confirmLabel: 'Delete',
      danger: true,
    }))) return;

    try {
      setDeletingId(highlightId);
      await onDeleteHighlight(highlightId);
    } catch (error) {
      console.error('Error deleting highlight:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleReattach = async () => {
    try {
      setIsReattaching(true);
      await onReattach();
    } catch (error) {
      console.error('Error re-attaching highlights:', error);
    } finally {
      setIsReattaching(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!(await confirm({
      title: 'Delete all highlights?',
      message: 'Every highlight on this article will be removed.',
      confirmLabel: 'Delete all',
      danger: true,
    }))) return;

    try {
      setIsDeletingAll(true);
      await onDeleteAll();
    } catch (error) {
      console.error('Error deleting all highlights:', error);
    } finally {
      setIsDeletingAll(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-surface shadow-2xl z-50 overflow-y-auto transition-transform duration-300 transform translate-x-0">
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-theme-border-subtle px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-heading">Highlights</h2>
            <button
              onClick={onClose}
              className="btn-clay-icon"
              title="Close sidebar"
              aria-label="Close highlights"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Count badge */}
          <div className="flex items-center text-sm text-secondary-text">
            <svg className="w-4 h-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>
              {highlights.length} {highlights.length === 1 ? 'highlight' : 'highlights'}
            </span>
          </div>
        </div>

        {/* Detachment Warning */}
        {detachmentInfo && detachmentInfo.detached_highlight_count > 0 && (
          <div className="notice-clay-warning mx-6 my-4">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold mb-1">
                  Summary Changed
                </h3>
                <p className="text-sm mb-3">{detachmentInfo.message}</p>
                <button
                  onClick={handleReattach}
                  disabled={isReattaching || isOffline}
                  className="btn-clay-secondary btn-clay-sm inline-flex items-center"
                  title={isOffline ? "Connect to internet to do this" : undefined}
                >
                  {isReattaching ? (
                    <>
                      <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                      Re-attaching...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Re-attach Highlights
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="spinner-fun"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && highlights.length === 0 && (
          <EmptyState
            icon={
              <svg className="w-16 h-16 text-tertiary-text mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            title="No Highlights Yet"
            description="Select text in the summary to create your first highlight"
            className="flex flex-col items-center justify-center py-12 px-6 text-center"
          />
        )}

        {/* Highlights List */}
        {!isLoading && highlights.length > 0 && (
          <div className="divide-y divide-theme-border-subtle">
            {highlights.map((highlight) => {
              const isActive = highlight.is_active !== false;
              const isDeleting = deletingId === highlight.id;

              return (
                <div
                  key={highlight.id}
                  className={`px-6 py-4 hover:bg-surface-alt/50 transition-colors cursor-pointer ${
                    !isActive ? 'opacity-60' : ''
                  }`}
                  onClick={() => onHighlightClick && onHighlightClick(highlight)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      {/* Exact text */}
                      <p className="text-sm text-heading line-clamp-3 mb-2">
                        {highlight.exact_text}
                      </p>

                      {/* Metadata */}
                      <div className="flex items-center text-xs text-secondary-text space-x-2">
                        <time dateTime={highlight.created_at}>
                          {formatFullDate(highlight.created_at)}
                        </time>
                        {!isActive && (
                          <>
                            <span>•</span>
                            <span className="text-yellow-600 font-medium">Outdated</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Delete button */}
                    <button
                      onClick={(e) => handleDelete(highlight.id, e)}
                      disabled={isDeleting}
                      className="btn-clay-icon flex-shrink-0 hover:text-fun-coral"
                      title="Delete highlight"
                      aria-label="Delete highlight"
                    >
                      {isDeleting ? (
                        <div className="spinner-fun-sm w-4 h-4"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Clear All Button */}
        {!isLoading && highlights.length > 0 && (
          <div className="p-4 border-t border-theme-border-subtle">
            <button
              onClick={handleDeleteAll}
              disabled={isDeletingAll || isOffline}
              className="btn-clay-danger w-full inline-flex items-center justify-center"
              title={isOffline ? "Connect to internet to do this" : "Delete all highlights"}
            >
              {isDeletingAll ? (
                <>
                  <div className="spinner-fun-sm w-4 h-4 mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear All Highlights
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default HighlightsSidebar;
